import {useLayoutEffect, RefObject, useRef} from 'react';

interface BoundingBoxValues {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

/**
 * This layout effect hook calculates the bounding box of the SVG’s contents and sets its
 * viewBox so that there’s no extra padding. Also adds a CSS class to the <svg> if defined.
 * Does nothing if the container ref does not contain an <svg> descendant.
 *
 * Usage:
 * ```
 * function MyComponent() {
 *   const containerRef = useResizeSVG<HTMLDivElement>(css(styles.svg));
 *   return (
 *     <div ref={containerRef}>
 *       <svg></svg>
 *     </div>
 *   );
 * }
 * ```
 *
 * @param {Type} T Type of the <svg>s parent element, e.g. `HTMLDivElement`
 * @param svgClassName CSS class to give to the <svg>, useful for styling with Aphrodite
 * @returns React ref for the HTML element that is the _parent_ (or ancestor) of the <svg> element
 */
export default function useResizeSVG<T extends HTMLElement>(svgClassName?: string): RefObject<T> {
  const svgContainerRef = useRef<T>(null);

  useLayoutEffect(() => {
    const svg = svgContainerRef.current?.querySelector('svg');
    if (!svg) {
      return;
    }

    // find the min and max bounding coordinates across all children
    const {xMin, xMax, yMin, yMax} = Array.from(svg.children).reduce(
      (acc: BoundingBoxValues, el: SVGGraphicsElement): BoundingBoxValues => {
        if (el.getBBox) {
          const {x, y, width, height} = el.getBBox();
          // Skip any hidden elements which can skew the bounding box calculation.
          if (width === 0 && height === 0) {
            return acc;
          }
          acc.xMin = Math.min(acc.xMin, x);
          acc.xMax = Math.max(acc.xMax, x + width);
          acc.yMin = Math.min(acc.yMin, y);
          acc.yMax = Math.max(acc.yMax, y + height);
        }
        return acc;
      },
      {
        xMin: Number.MAX_VALUE,
        xMax: Number.MIN_VALUE,
        yMin: Number.MAX_VALUE,
        yMax: Number.MIN_VALUE,
      }
    );
    svg.setAttribute('viewBox', `${xMin} ${yMin} ${xMax - xMin} ${yMax - yMin}`);
    svg.removeAttribute('width');
    svg.removeAttribute('height');
    if (svgClassName !== undefined) {
      svg.setAttribute('class', svgClassName);
    }
  });

  return svgContainerRef;
}
